// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";
import { 
  Ticket,
  FileText,
  Calendar,
  MapPin
} from "lucide-react";
import { QRCodeSVG } from 'qrcode.react';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { parseLocalDate } from '@/utils/formatters';

interface PurchasedTicket {
  id: string;
  ticket_name: string;
  ticket_price: number;
  qr_code: string;
  is_redeemed: boolean;
  redeemed_at: string | null;
  created_at: string;
  event_id: string;
  registration_id: string;
  events: {
    id: string;
    name: string;
    start_date: string;
    end_date: string;
    venue_name: string;
  } | null;
}

const MyTicketsPage: React.FC = () => {
  const { profile } = useAuth();
  const [purchasedTickets, setPurchasedTickets] = useState<PurchasedTicket[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.user_id) {
      fetchTickets();
    }
  }, [profile?.user_id]);

  const fetchTickets = async () => {
    if (!profile?.user_id) return;

    try {
      setLoading(true);
      console.log('🔍 Querying spectator tickets for user:', profile.user_id);
      
      // Query purchased_tickets directly with event details
      const { data: tickets, error } = await supabase
        .from('purchased_tickets')
        .select(`
          id,
          ticket_name,
          ticket_price,
          qr_code,
          is_redeemed,
          redeemed_at,
          created_at,
          event_id,
          registration_id,
          events:event_id (
            id,
            name,
            start_date,
            end_date,
            venue_name
          )
        `)
        .eq('user_id', profile.user_id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching purchased tickets:', error);
        
        // Fallback: Try querying via registrations table
        console.log('🔄 Trying alternative query via registrations...');
        const { data: regs, error: regError } = await supabase
          .from('registrations')
          .select('id')
          .eq('user_id', profile.user_id);
          
        if (regError) {
          console.error('❌ Registrations query failed:', regError);
          return;
        }
        
        const regIds = regs?.map(r => r.id) || [];
        if (regIds.length > 0) {
          const { data: fallbackTickets, error: fallbackError } = await supabase
            .from('purchased_tickets')
            .select(`
              id,
              ticket_name,
              ticket_price,
              qr_code,
              is_redeemed,
              redeemed_at,
              created_at,
              event_id,
              registration_id,
              events:event_id (
                id,
                name,
                start_date,
                end_date,
                venue_name
              )
            `)
            .in('registration_id', regIds)
            .order('created_at', { ascending: false });
            
          if (!fallbackError && fallbackTickets) {
            const formattedTickets = fallbackTickets.map(ticket => ({
              ...ticket,
              events: Array.isArray(ticket.events) ? ticket.events[0] : ticket.events
            })) as PurchasedTicket[];
            setPurchasedTickets(formattedTickets);
          }
        }
        return;
      }

      console.log('✅ Found purchased tickets:', tickets);
      // Transform the data - events may be array or object depending on FK
      const formattedTickets = (tickets || []).map(ticket => ({
        ...ticket,
        events: Array.isArray(ticket.events) ? ticket.events[0] : ticket.events
      })) as PurchasedTicket[];
      
      setPurchasedTickets(formattedTickets);
    } catch (error) {
      console.error('❌ Unexpected error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTicket = async (ticket: PurchasedTicket) => {
    const qrData = ticket.qr_code || JSON.stringify({
      ticketId: ticket.id,
      eventId: ticket.event_id,
      eventName: ticket.events?.name || 'Event',
      ticketName: ticket.ticket_name,
      purchaseDate: ticket.created_at
    });

    try {
      // Generate QR code as data URL
      const qrCodeDataURL = await QRCode.toDataURL(qrData, {
        width: 150,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      // Create PDF
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      
      // Header - RecruitLook branding
      pdf.setFontSize(20);
      pdf.setTextColor(0, 46, 98); // #002E62
      pdf.text('RecruitLook', pageWidth / 2, 25, { align: 'center' });
      
      pdf.setFontSize(14);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Spectator Ticket', pageWidth / 2, 35, { align: 'center' });
      
      // Event Information
      pdf.setFontSize(18);
      pdf.setTextColor(0, 46, 98);
      pdf.text(ticket.events?.name || 'Event', pageWidth / 2, 55, { align: 'center' });
      
      const eventDate = parseLocalDate(ticket.events?.start_date || '');
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Date: ${eventDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}`, pageWidth / 2, 70, { align: 'center' });
      
      if (ticket.events?.venue_name) {
        pdf.text(`Venue: ${ticket.events.venue_name}`, pageWidth / 2, 80, { align: 'center' });
      }
      
      // Ticket Details
      pdf.setFontSize(14);
      pdf.setTextColor(0, 46, 98);
      pdf.text('Ticket Information', 20, 105);
      
      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Ticket Type: ${ticket.ticket_name}`, 20, 120);
      pdf.text(`Price: $${(ticket.ticket_price || 0).toFixed(2)}`, 20, 130);
      pdf.text(`Purchase Date: ${new Date(ticket.created_at).toLocaleDateString()}`, 20, 140);
      pdf.text(`Ticket ID: ${ticket.id.slice(0, 16)}...`, 20, 150);
      
      if (ticket.is_redeemed) {
        pdf.setTextColor(255, 0, 0);
        pdf.text('Status: REDEEMED', 20, 160);
        pdf.setTextColor(0, 0, 0);
      }
      
      // Customer Information
      if (profile?.name || profile?.email) {
        pdf.setFontSize(14);
        pdf.setTextColor(0, 46, 98);
        pdf.text('Customer Information', 20, 180);
        
        pdf.setFontSize(11);
        pdf.setTextColor(0, 0, 0);
        if (profile.name) {
          pdf.text(`Name: ${profile.name}`, 20, 195);
        }
        if (profile.email) {
          pdf.text(`Email: ${profile.email}`, 20, 205);
        }
      }
      
      // QR Code
      pdf.setFontSize(14);
      pdf.setTextColor(0, 46, 98);
      pdf.text('Check-in QR Code', pageWidth / 2, 230, { align: 'center' });
      
      // Add QR code image
      pdf.addImage(qrCodeDataURL, 'PNG', (pageWidth - 60) / 2, 240, 60, 60);
      
      // Instructions
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Present this ticket at event check-in', pageWidth / 2, 315, { align: 'center' });
      pdf.text('Keep this ticket accessible on your mobile device or print a copy', pageWidth / 2, 325, { align: 'center' });
      
      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(128, 128, 128);
      pdf.text('Generated by RecruitLook - www.recruitlook.com', pageWidth / 2, 340, { align: 'center' });
      
      // Save PDF
      const customerName = profile?.name || 'Customer';
      const eventName = ticket.events?.name || 'Event';
      const fileName = `${eventName.replace(/[^a-z0-9]/gi, '_')}_SpectatorTicket_${customerName.replace(/[^a-z0-9]/gi, '_')}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF ticket:', error);
    }
  };

  // Group tickets by event
  const ticketsByEvent = purchasedTickets.reduce((acc, ticket) => {
    const eventName = ticket.events?.name || 'Unknown Event';
    if (!acc[eventName]) {
      acc[eventName] = [];
    }
    acc[eventName].push(ticket);
    return acc;
  }, {} as Record<string, PurchasedTicket[]>);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-lg">Loading tickets...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-heading font-bold text-primary-700">
            My Tickets
          </h1>
          <p className="text-gray-600">
            Your purchased spectator tickets with QR codes for event check-in
          </p>
        </div>
      </div>

      {/* Tickets Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5" />
            Purchased Tickets ({purchasedTickets.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(ticketsByEvent).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(ticketsByEvent).map(([eventName, tickets]) => {
                const firstTicket = tickets[0];
                const eventDate = parseLocalDate(firstTicket.events?.start_date || '');
                
                return (
                  <div key={eventName} className="border rounded-lg p-6">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h3 className="font-semibold text-xl mb-2">{eventName}</h3>
                        <div className="flex items-center gap-4 text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            <span>
                              {eventDate.toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                          {firstTicket.events?.venue_name && (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              <span>{firstTicket.events.venue_name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-lg px-3 py-1">
                        {tickets.length} ticket{tickets.length > 1 ? 's' : ''}
                      </Badge>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {tickets.map((ticket) => {
                        const qrData = ticket.qr_code || JSON.stringify({
                          ticketId: ticket.id,
                          eventId: ticket.event_id,
                          eventName: ticket.events?.name || 'Event',
                          ticketName: ticket.ticket_name,
                          purchaseDate: ticket.created_at
                        });

                        return (
                          <div key={ticket.id} className="border rounded-lg p-4 bg-gray-50">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h4 className="font-medium text-lg">{ticket.ticket_name}</h4>
                                <p className="text-sm font-medium text-green-600">
                                  ${(ticket.ticket_price || 0).toFixed(2)}
                                </p>
                              </div>
                              <Badge 
                                variant="default" 
                                className={ticket.is_redeemed 
                                  ? "bg-gray-100 text-gray-800" 
                                  : "bg-green-100 text-green-800"
                                }
                              >
                                {ticket.is_redeemed ? 'Redeemed' : 'Ready'}
                              </Badge>
                            </div>

                            <div className="flex flex-col items-center space-y-4">
                              <div className="bg-white p-3 rounded border shadow-sm">
                                <QRCodeSVG 
                                  value={qrData}
                                  size={140}
                                />
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadTicket(ticket)}
                                className="w-full"
                              >
                                <FileText className="h-4 w-4 mr-2" />
                                Download Ticket
                              </Button>
                            </div>

                            <div className="mt-4 pt-4 border-t text-xs text-gray-500">
                              <p>Purchased: {new Date(ticket.created_at).toLocaleDateString()}</p>
                              <p className="mt-1">Ticket ID: {ticket.id.slice(0, 8)}...</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Ticket className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
              <p>Your purchased spectator tickets will appear here</p>
              <p className="text-sm mt-1">Purchase tickets from event pages to get started</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyTicketsPage;